import { Link } from "react-router-dom";
import { workshops } from "../../../content";
import type { Workshop } from "../../../types/content";
import { resolvePublishedImages, workshopCertificateImage } from "../../../utils/workshopImages";
import type { ResolvedWorkshopImage } from "../../../utils/workshopImages";
import WorkshopGlimpse from "./WorkshopGlimpse";
import { monthLabel } from "./Workshops";

/**
 * ============================================================================
 * Workshops & Training — the Home teaser (D-080), rebuilt as a media card
 * (D-099).
 * ============================================================================
 *
 * `/workshops` has been a pre-rendered route since D-076 and reachable only
 * from a dropdown. Every other route on this site has a Home strip that says
 * what it holds and links to it; this one did not, so the one section of the
 * documentary about TEACHING the work was the one section a reader had to go
 * looking for.
 *
 * CONTENT-LAYER DRIVEN, AND ORDERED BY THE LAYER. `workshops` arrives from the
 * loader already sorted newest-first by `date` — derived there rather than
 * trusted from file order (D-076) — so this renders `workshops` as given and
 * adds no ordering opinion of its own. Three of four entries are shown; the
 * `slice` below keeps the strip at three however many the layer grows to, which
 * is what the "See every session" link is for. That is a deliberate difference
 * from ProductsTeaser, where holding the count at four is load-bearing on a
 * four-column grid.
 *
 * ----------------------------------------------------------------------------
 * WHAT WAS WRONG WITH IT, MEASURED RATHER THAN ASSERTED
 * ----------------------------------------------------------------------------
 * The strip rendered three text cards, one of which carried the certificate
 * cropped to `max-h-56` at `object-cover object-top`. At 1280 that produced a
 * 1042 px section in which two of the three cards were **half empty** — the row
 * stretches to the tallest card and the two without a picture had nothing to
 * fill it with — and the third card's picture was cut off through the signatory
 * block. That last one is not only a look: D-092's standard for these files is
 * that the typed signatory name and office are the things the redaction exists
 * to PRESERVE, so cropping them away on Home while the same file renders whole
 * two clicks later is incoherent.
 *
 * ----------------------------------------------------------------------------
 * THE MEDIA BAND SHOWS A PHOTOGRAPH, AND THE CERTIFICATE ONLY WHERE THERE IS NO
 * PHOTOGRAPH
 * ----------------------------------------------------------------------------
 * This is the one visible content decision in the file and it goes the other
 * way from the old code, so it is worth stating plainly.
 *
 * A landscape band cannot show a portrait certificate (1079x1525) without
 * either cropping through the signatory block — the defect above — or shrinking
 * it to the point where nothing on it is legible. A session photograph is
 * 1600x1067 and fits the band it is being asked to fill. So the band takes the
 * first published photograph, and falls back to a published certificate only
 * for a workshop that has no photographs at all.
 *
 * **THE CREDENTIAL DOES NOT DISAPPEAR FROM HOME**: it keeps its chip in the
 * meta row and its issuer-and-year line in the footer, both derived from the
 * same two-key gate, and it renders whole and uncropped on /workshops and on
 * its own detail route — which is where "See every session and the credential"
 * has always gone.
 *
 * NO TEXT IS SET OVER THE PHOTOGRAPH, and that is a rule rather than a
 * preference. Contrast is computed against ONE ground colour; a photograph is
 * not one colour, so a pill floated over the band would be a label no AA sweep
 * on this site can score (D-051, and D-057's finding that the translucent menu
 * over a photograph reported 0 failures on the broken build). Everything that
 * has to be READ sits on the card face below the band.
 *
 * ----------------------------------------------------------------------------
 * THE WHOLE CARD IS CLICKABLE — SAME MECHANISM AS THE INDEX, ON PURPOSE
 * ----------------------------------------------------------------------------
 * The heading carries the `<Link to={/workshops/<slug>}>` and it is stretched
 * over the card with `after:absolute after:inset-0`; `relative` on the
 * `<article>` is what that resolves against. A mouse gets the whole card, a
 * screen reader hears ONE link whose accessible name is the workshop's title,
 * and a keyboard gets one tab stop.
 *
 * A wrapping `<Link>` would be VALID here — unlike the index card, this one
 * contains no button — and it is still not used. Two implementations of "the
 * card navigates" is two places the behaviour can drift, and the index card
 * cannot use the simpler one (an `<a>` around a `<button>` is rewritten by the
 * parser and D-042 measured one such element disabling hydration site-wide).
 * The reason to match is that they must stay matched.
 *
 * ----------------------------------------------------------------------------
 * THE IMAGE COUNT IS A BUDGET, NOT AN ACCIDENT
 * ----------------------------------------------------------------------------
 * These frames are 204-269 kB each, and D-086 is the record of seven of them
 * requested at once producing five decode failures on a sweep that had reported
 * 675/0 on every run it had ever made. So a card draws THREE images — the band,
 * plus two glimpse frames — and the rest of the set is a `+N` tile rather than
 * more pictures. Nine on Home, which is what /workshops has loaded since D-095.
 * Every one is `loading="lazy"`; this section sits ~14,700 px down, so a reader
 * who never reaches it pays nothing.
 *
 * `skip={1}` on the glimpse is why the band and the strip do not show the same
 * picture twice.
 */

/** Newest first, as the loader ordered them; the strip shows three. */
const TEASER_COUNT = 3;

/** Frames the card previews BESIDE the band, before it stops and counts. */
const GLIMPSE_COUNT = 2;

const TeaserCard = ({ workshop }: { workshop: Workshop }): JSX.Element => {
	const photographs: ResolvedWorkshopImage[] = resolvePublishedImages(workshop.images);

	/* BOTH KEYS, as everywhere these pictures are read: `status` says whether an
	   image MAY be shown and the export map says whether one EXISTS. This
	   component cannot be made to show an unredacted scan by editing it, because
	   the file it would need is exported from no module (D-079). */
	const certificate = workshop.certificate?.status === "published" ? workshop.certificate : null;
	const credentialImage = certificate ? workshopCertificateImage(certificate.id) : undefined;

	/* Photograph first, certificate only as the fallback — see the note above. */
	const band: string | undefined = photographs.length ? photographs[0].binary : credentialImage;

	return (
		<article className="card-shell group relative">
			<div className="card-face flex h-full flex-col overflow-hidden">
				{band && (
					/* THE BAND RESERVES ITS BOX AT EVERY WIDTH. A replaced element
					   with no declared ratio is 0-height before decode and full
					   height after, which moves everything below it — D-083
					   measured 179.7-289.1 px of exactly that on the hero. Three
					   ratios because the card's width is not a function of the
					   viewport's (D-080): one column below 640, one column of a
					   wide card to 1024, three columns above it. */
					<span className="block aspect-[16/9] w-full overflow-hidden sm:aspect-[21/9] lg:aspect-[16/10]">
						<img
							src={band}
							alt=""
							aria-hidden="true"
							loading="lazy"
							decoding="async"
							className="block h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
						/>
					</span>
				)}

				<div className="flex flex-1 flex-col p-6">
					<div className="flex flex-wrap items-center gap-3">
						<span className="pill-tint pill-tint-upper border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] text-[var(--cat-indigo-text)]">
							{workshop.role}
						</span>
						<span className="text-sm font-semibold text-[var(--text-muted)]">
							{monthLabel(workshop.date, "long")}
						</span>
						{certificate && (
							<span className="pill-tint border-[var(--cat-emerald-border)] bg-[var(--cat-emerald-fill)] text-[var(--cat-emerald-text)]">
								Credential
							</span>
						)}
					</div>

					<h3 className="mt-4 text-lg font-bold leading-snug text-[var(--text-strong)]">
						<Link
							to={`/workshops/${workshop.slug}`}
							className="rounded-[4px] after:absolute after:inset-0 after:rounded-[1rem] after:content-[''] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
						>
							{workshop.title}
						</Link>
					</h3>

					<p className="mt-2 text-sm font-medium text-[var(--text-muted)]">{workshop.host}</p>

					{/* `dateDetail` carries what a month cannot — "Jan 13-31, 40-hour
					    program". It is the layer's string, rendered where the layer
					    has one and absent where it does not; the index shows it
					    parenthesised beside the month, which is too long for a
					    teaser pill. */}
					{workshop.dateDetail && (
						<p className="mt-1 text-xs text-[var(--text-faint)]">{workshop.dateDetail}</p>
					)}

					<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">{workshop.description}</p>

					{/* `flex-1` HERE, not on the description. It is the spacer that
					    pins the glimpse and the footer to the bottom of a stretched
					    card, which is what makes three cards of different text
					    lengths read as a row rather than as three ragged columns.
					    The row stretches deliberately — these cards do not expand,
					    so uniform heights cost nothing, and `items-start` alone
					    would not change it anyway: `.card-shell` carries
					    `height: 100%`, which resolves against the grid AREA and
					    re-inflates a start-aligned item (D-080). */}
					<div className="flex-1" />

					<WorkshopGlimpse
						images={photographs}
						skip={1}
						max={GLIMPSE_COUNT}
						className="mt-5 grid grid-cols-3 gap-2.5"
						frameClassName="block aspect-[4/3] overflow-hidden rounded-[10px]"
					/>

					{certificate && (
						<p className="mt-4 text-sm text-[var(--text-faint)]">
							Certificate of {certificate.issuer}, {certificate.issuedDate.slice(0, 4)}
						</p>
					)}

					{/* The affordance. `aria-hidden`, because the heading link above
					    already carries this destination and announcing it twice
					    gives a screen-reader user two links to one page. */}
					<p
						aria-hidden="true"
						className="mt-4 text-sm font-medium text-[var(--accent-link)] transition-colors duration-200"
					>
						Read more →
					</p>
				</div>
			</div>
		</article>
	);
};

export default function WorkshopsTeaser(): JSX.Element | null {
	/* A section with nothing in it must not ship (Constitution II.4). It cannot
	   happen today — the layer holds four — and it is one line. */
	if (!workshops.length) return null;

	const shown: Workshop[] = workshops.slice(0, TEASER_COUNT);

	return (
		<section className="section-shell" id="workshops" aria-labelledby="workshops-teaser-heading">
			<div className="section-rail">
				<p className="section-eyebrow">Teaching &amp; Enablement</p>

				<h2 id="workshops-teaser-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Workshops &amp; Training
					</span>
				</h2>

				{/* NO COUNT IN THIS LINE, AND IT USED TO CARRY ONE. It read "with
				    the one credential that documents them" — true today, and an
				    assertion about how many credentials the layer holds, made in
				    the one kind of string nothing checks (D-066). The second
				    workshop to arrive with a certificate would have made Home say
				    something false without anyone editing Home. */}
				<p className="section-standfirst">
					Sessions delivered to practitioners and to students — what each one covered, and the record behind
					it.
				</p>

				<div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
					{shown.map((workshop: Workshop) => (
						<TeaserCard key={workshop.id} workshop={workshop} />
					))}
				</div>

				<div className="mt-10">
					<Link
						to="/workshops"
						className="inline-block min-h-[24px] text-sm font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
					>
						See every session and the credential &rarr;
					</Link>
				</div>
			</div>
		</section>
	);
}
